/*
 * The reader's picture requests (rendering plan, slice 3). Three things are asked, each only when a
 * picture would be seen:
 *
 * - this person's preference for the workspace (`GET .../image-preference`): whether pictures from the
 *   sender are hidden. Until it is known nothing else is asked, so a hidden picture is never requested;
 * - a message's manifest (`GET .../messages/:message/images`), once the message is open and its frame
 *   is near the screen;
 * - each picture the frame will show (`.../images/:asset?visible=1`), as bytes turned into a `blob:`
 *   address. Folded quoted history, hidden pictures and closed messages are never asked for, and a
 *   request whose picture leaves the screen's plan is cancelled.
 *
 * A failure is reduced to a state (`AssetState`); its transport detail is never shown.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { type AssetState, type ImageAsset, ImageManifestSchema } from "./mail-images.ts";

const workspaceApi = (workspaceId: string) =>
  `/api/work/workspaces/${encodeURIComponent(workspaceId)}`;

const PreferenceSchema = z.strictObject({ blockExternal: z.boolean() });
export type ImagePreference = {
  readonly blockExternal: boolean;
  /** False where the server has no preference to read or change: the default, show, holds. */
  readonly available: boolean;
};

const preferenceKey = (workspaceId: string) => ["image-preference", workspaceId] as const;

async function readPreference(workspaceId: string): Promise<ImagePreference> {
  const response = await fetch(`${workspaceApi(workspaceId)}/image-preference`, {
    credentials: "same-origin",
  });
  if (!response.ok) return { blockExternal: false, available: false };
  const parsed = PreferenceSchema.safeParse(await response.json().catch(() => null));
  return parsed.success
    ? { blockExternal: parsed.data.blockExternal, available: true }
    : { blockExternal: false, available: false };
}

/** This person's picture preference in the workspace; `settled` is false until it is known. */
export function useImagePreference(workspaceId: string, enabled = true) {
  const query = useQuery({
    queryKey: preferenceKey(workspaceId),
    enabled: enabled && workspaceId !== "",
    retry: false,
    staleTime: 60_000,
    queryFn: () => readPreference(workspaceId),
  });
  return {
    preference: query.data ?? { blockExternal: false, available: false },
    settled: query.data !== undefined,
  };
}

/** Changes the preference; every open message reads it again and asks for what it now shows. */
export function useSetImagePreference(workspaceId: string) {
  const client = useQueryClient();
  return async (blockExternal: boolean): Promise<boolean> => {
    const response = await fetch(`${workspaceApi(workspaceId)}/image-preference`, {
      method: "PUT",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ blockExternal }),
    });
    const parsed = PreferenceSchema.safeParse(await response.json().catch(() => null));
    if (!response.ok || !parsed.success) return false;
    client.setQueryData(preferenceKey(workspaceId), {
      blockExternal: parsed.data.blockExternal,
      available: true,
    });
    return true;
  };
}

/** The base of a message's picture routes. */
export const messageImagesPath = (workspaceId: string, planId: string, messageId: string) =>
  `${workspaceApi(workspaceId)}/plans/${encodeURIComponent(planId)}/messages/${encodeURIComponent(messageId)}/images`;

/** A message's manifest, asked once it is wanted; `failed` means its pictures cannot be shown here. */
export function useImageManifest(path: string, enabled: boolean) {
  const query = useQuery({
    queryKey: ["mail-images", path],
    enabled,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      const response = await fetch(path, { credentials: "same-origin" });
      if (!response.ok) throw new Error("manifest_unavailable");
      return ImageManifestSchema.parse(await response.json()).assets;
    },
  });
  return { assets: query.data ?? null, failed: query.isError };
}

const STATUS: Record<number, AssetState> = {
  409: { status: "blocked" },
  429: { status: "busy" },
};

const RefusalSchema = z.object({ error: z.string() });

/** A 404 says which in its body: a recognised format the reader does not show, or no picture at all. */
async function refused(response: Response): Promise<AssetState> {
  if (response.status !== 404) return STATUS[response.status] ?? { status: "failed" };
  const body = RefusalSchema.safeParse(await response.json().catch(() => null));
  return body.success && body.data.error === "image_unsupported"
    ? { status: "unsupported" }
    : { status: "unavailable" };
}

async function fetchAsset(url: string, signal: AbortSignal): Promise<AssetState | null> {
  try {
    const response = await fetch(url, { credentials: "same-origin", signal });
    if (!response.ok) return await refused(response);
    const blob = await response.blob();
    if (!blob.type.startsWith("image/")) return { status: "failed" };
    return { status: "shown", url: URL.createObjectURL(blob) };
  } catch {
    return signal.aborted ? null : { status: "failed" };
  }
}

const RETRY_BUSY_MS = 1_500;

type Fetching = {
  readonly flights: Map<string, AbortController>;
  readonly made: Set<string>;
  readonly setStates: (
    change: (now: ReadonlyMap<string, AssetState>) => ReadonlyMap<string, AssetState>,
  ) => void;
  readonly path: string;
  readonly show: boolean;
};

/** Asks for one picture; a busy proxy is asked once more after a pause, then left busy. */
function startFetch(context: Fetching, asset: ImageAsset, retried: boolean): void {
  const { flights } = context;
  const controller = new AbortController();
  flights.set(asset.id, controller);
  const override = context.show && asset.kind === "external" ? "&show=1" : "";
  context.setStates((now) => new Map(now).set(asset.id, { status: "loading" }));
  void fetchAsset(`${context.path}/${asset.id}?visible=1${override}`, controller.signal).then(
    (state) => {
      if (state === null || flights.get(asset.id) !== controller) return;
      if (state.status === "busy" && !retried) {
        setTimeout(() => {
          if (flights.get(asset.id) === controller) startFetch(context, asset, true);
        }, RETRY_BUSY_MS);
        return;
      }
      flights.delete(asset.id);
      if (state.status === "shown") context.made.add(state.url);
      context.setStates((now) => new Map(now).set(asset.id, state));
    },
  );
}

/** Lets go of the pictures no longer wanted: a folded quote's, a closed message's. */
function release(
  states: ReadonlyMap<string, AssetState>,
  wanted: ReadonlySet<string>,
  made: Set<string>,
): void {
  for (const [id, state] of states)
    if (!wanted.has(id) && state.status === "shown") {
      URL.revokeObjectURL(state.url);
      made.delete(state.url);
    }
}

/** Asked again only by Try again, or by showing what the preference hid; never by a re-render. */
function asksAgain(
  status: AssetState["status"] | undefined,
  why: { retry: boolean; reveal: boolean },
): boolean {
  if (status === undefined) return true;
  if (why.retry && (status === "failed" || status === "busy")) return true;
  return why.reveal && status === "blocked";
}

/**
 * Fetches `wanted` through the proxy and reports each one's state. An asset that stops being wanted is
 * cancelled and forgotten, its picture released; `show` asks for this message only what the preference
 * hides. `attempt` changes to ask again for what failed or was busy.
 */
export function useAssets(
  path: string,
  wanted: readonly ImageAsset[],
  options: { show: boolean; attempt: number },
): ReadonlyMap<string, AssetState> {
  const [states, setStates] = useState<ReadonlyMap<string, AssetState>>(() => new Map());
  const flights = useRef(new Map<string, AbortController>());
  const made = useRef(new Set<string>());
  const asked = useRef({ attempt: options.attempt, show: options.show });
  const key = wanted.map((asset) => asset.id).join(",");
  const { show, attempt } = options;

  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` names `wanted`; `states` is read, not watched
  useEffect(() => {
    const ids = new Set(key ? key.split(",") : []);
    const why = { retry: asked.current.attempt !== attempt, reveal: show && !asked.current.show };
    asked.current = { attempt, show };
    for (const [id, controller] of flights.current)
      if (!ids.has(id)) {
        controller.abort();
        flights.current.delete(id);
      }
    release(states, ids, made.current);
    setStates((now) => {
      const next = new Map([...now].filter(([id]) => ids.has(id)));
      return next.size === now.size ? now : next;
    });
    const context = { flights: flights.current, made: made.current, setStates, path, show };
    for (const asset of wanted)
      if (!flights.current.has(asset.id) && asksAgain(states.get(asset.id)?.status, why))
        startFetch(context, asset, false);
  }, [key, path, show, attempt]);

  useEffect(() => {
    const live = flights.current;
    const urls = made.current;
    return () => {
      for (const controller of live.values()) controller.abort();
      live.clear();
      for (const url of urls) URL.revokeObjectURL(url);
      urls.clear();
    };
  }, []);
  return states;
}

/** True once `element` is within a screen of the viewport; where nothing can tell, it is. */
export function useNear(element: React.RefObject<Element | null>): boolean {
  const [near, setNear] = useState(typeof IntersectionObserver === "undefined");
  useEffect(() => {
    const target = element.current;
    if (!target || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: "600px 0px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [element]);
  return near;
}
